import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { IntegrationIcon } from '@/components/integrations/IntegrationIcon'
import { toast } from 'sonner'
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Plug,
  CheckCircle2,
  Download
} from 'lucide-react'
import type { Project, Integration } from '@/types/conversations'

interface LiveIntegrationsSectionProps {
  project: Project
}

export function LiveIntegrationsSection({ project }: LiveIntegrationsSectionProps) {
  const { t } = useTranslation('conversations')
  const [expandedIntegration, setExpandedIntegration] = useState<number | null>(null)
  const [visibleValues, setVisibleValues] = useState<Set<number>>(new Set())
  const [envVars, setEnvVars] = useState<Record<number, Record<string, string>>>({})
  const [isSubmitting, setIsSubmitting] = useState<number | null>(null)
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false)
  const [integrationToDisconnect, setIntegrationToDisconnect] = useState<Integration | null>(null)

  // Filter integrations that have sandbox variables (these need live credentials)
  const sandboxIntegrations = project.integrations.filter(
    integration => integration.sandbox_variables.length > 0
  )

  const toggleValueVisibility = (id: number) => {
    setVisibleValues(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleImportSingleIntegration = async (integrationId: number) => {
    setIsSubmitting(integrationId)

    try {
      const response = await fetch(
        `/projects/${project.id}/integrations/import_to_live`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token':
              document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          },
          body: JSON.stringify({ integration_ids: [integrationId] }),
        }
      )

      if (response.ok) {
        toast.success(t('integrations.toasts.importLiveSuccess'))
        router.reload()
      } else {
        const data = await response.json()
        toast.error(`${t('integrations.toasts.importLiveError')}: ${data.errors?.join(', ') || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Import error:', error)
      toast.error(t('integrations.toasts.importLiveError'))
    } finally {
      setIsSubmitting(null)
    }
  }

  const handleManualConnect = async (integration: Integration) => {
    const integrationEnvVars = envVars[integration.id] || {}

    // Check if all required fields are filled
    const template = integration.template
    const requiredFields = template?.environment_variables.filter(v => v.required) || []
    const missingFields = requiredFields.filter(field => !integrationEnvVars[field.key])

    if (missingFields.length > 0) {
      toast.error(t('integrations.toasts.fillRequired'))
      return
    }

    setIsSubmitting(integration.id)

    try {
      // Create live secrets via the existing connect endpoint
      const response = await fetch(`/projects/${project.id}/integrations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token':
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          integration_type: integration.integration_type,
          save_to_organization: false,
          environment_variables: integrationEnvVars,
          is_live: true
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(t('integrations.toasts.configureSuccess'))
        setEnvVars(prev => {
          const newVars = { ...prev }
          delete newVars[integration.id]
          return newVars
        })
        router.reload()
      } else {
        toast.error(data.errors?.join(', ') || t('integrations.toasts.configureError'))
      }
    } catch (error) {
      console.error('Connect error:', error)
      toast.error(t('integrations.toasts.configureError'))
    } finally {
      setIsSubmitting(null)
    }
  }

  const handleDisconnectLiveClick = (integration: Integration) => {
    setIntegrationToDisconnect(integration)
    setShowDisconnectDialog(true)
  }

  const handleConfirmDisconnect = async () => {
    if (!integrationToDisconnect) return

    setShowDisconnectDialog(false)
    setIsSubmitting(integrationToDisconnect.id)

    try {
      const response = await fetch(`/projects/${project.id}/integrations/${integrationToDisconnect.id}/disconnect_live`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token':
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(t('integrations.toasts.disconnectLiveSuccess'))
        router.reload()
      } else {
        toast.error(data.errors?.join(', ') || t('integrations.toasts.disconnectLiveError'))
      }
    } catch (error) {
      console.error('Disconnect error:', error)
      toast.error(t('integrations.toasts.disconnectLiveError'))
    } finally {
      setIsSubmitting(null)
      setIntegrationToDisconnect(null)
    }
  }

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('integrations.liveSection.title')}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
          {t('integrations.liveSection.description')}
        </p>
      </div>

      {sandboxIntegrations.length === 0 ? (
        <div className="border border-dashed rounded-lg p-8 text-center bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
            <Plug className="h-6 w-6 text-gray-400 dark:text-gray-500" />
          </div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
            {t('integrations.liveSection.noSandbox')}
          </h4>
        </div>
      ) : (
        <div className="space-y-2">
          {sandboxIntegrations.map((integration) => {
            const isExpanded = expandedIntegration === integration.id
            const template = integration.template
            const hasLiveVars = integration.has_live_variables
            const integrationVars = envVars[integration.id] || {}

            return (
              <div key={integration.id} className="border rounded-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Card Header */}
                <button
                  type="button"
                  onClick={() => setExpandedIntegration(isExpanded ? null : integration.id)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <IntegrationIcon integrationType={integration.integration_type} />
                    <div className="text-left">
                      <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100">
                        {template?.name || integration.integration_type}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {hasLiveVars
                          ? t('integrations.liveSection.liveSecretCount', { count: integration.live_variables.length })
                          : t('integrations.liveSection.notConfigured')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {hasLiveVars ? (
                      <Badge variant="default" className="text-xs bg-green-600">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        {t('integrations.status.connected')}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800">
                        {t('integrations.status.notConnected')}
                      </Badge>
                    )}
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t px-4 py-4 bg-gray-50 dark:bg-gray-800">
                    {hasLiveVars ? (
                      // Show read-only live variables
                      <div className="space-y-2">
                        {integration.live_variables.map((envVar) => {
                        const templateVar = template?.environment_variables.find(
                          v => v.key === envVar.key
                        )

                        return (
                          <div
                            key={envVar.id}
                            className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-3"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <code className="text-xs font-semibold text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-600 px-2 py-0.5 rounded">
                                    {envVar.key}
                                  </code>
                                  {templateVar?.required && (
                                    <span className="text-xs text-red-600 dark:text-red-400 font-medium">*</span>
                                  )}
                                </div>
                                {templateVar?.label && (
                                  <div className="text-xs text-gray-700 dark:text-gray-300 mb-1">
                                    {templateVar.label}
                                  </div>
                                )}
                                {(envVar.description || templateVar?.description) && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 leading-relaxed">
                                    {envVar.description || templateVar?.description}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-xs font-mono text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded break-all max-w-[300px]">
                                  {visibleValues.has(envVar.id) ? envVar.value : '••••••••'}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => toggleValueVisibility(envVar.id)}
                                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0"
                                >
                                  {visibleValues.has(envVar.id) ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                        })}

                        {/* Disconnect button for live credentials */}
                        <Button
                          type="button"
                          variant="destructive"
                          onClick={() => handleDisconnectLiveClick(integration)}
                          disabled={isSubmitting === integration.id}
                          className="w-full mt-4"
                        >
                          {isSubmitting === integration.id ? t('integrations.buttons.disconnecting') : t('integrations.buttons.disconnectLive')}
                        </Button>
                      </div>
                    ) : (
                      // Show connection form for integrations without live vars
                      <div className="space-y-4">
                        {/* Quick import option */}
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                          <p className="text-xs text-blue-800 dark:text-blue-300 mb-3">
                            {t('integrations.liveSection.importHint')}
                          </p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleImportSingleIntegration(integration.id)}
                            disabled={isSubmitting === integration.id}
                            className="w-full"
                          >
                            <Download className="h-4 w-4 mr-2" />
                            {isSubmitting === integration.id ? t('integrations.liveSection.importing') : t('integrations.liveSection.importButton')}
                          </Button>
                        </div>

                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                          </div>
                          <div className="relative flex justify-center text-xs">
                            <span className="bg-gray-50 dark:bg-gray-800 px-2 text-gray-500 dark:text-gray-400">{t('integrations.liveSection.orEnterNew')}</span>
                          </div>
                        </div>

                        {/* Manual entry form */}
                        <div className="space-y-3">
                          {template?.environment_variables.map((envVar) => (
                            <div key={envVar.key} className="space-y-1">
                              <Label htmlFor={`live-${integration.id}-${envVar.key}`} className="text-sm text-gray-900 dark:text-gray-100">
                                {envVar.label}
                                {envVar.required && <span className="text-red-600 dark:text-red-400 ml-1">*</span>}
                              </Label>
                              <Input
                                id={`live-${integration.id}-${envVar.key}`}
                                type={envVar.sensitive ? 'password' : 'text'}
                                placeholder={envVar.placeholder}
                                value={integrationVars[envVar.key] || ''}
                                onChange={(e) => setEnvVars({
                                  ...envVars,
                                  [integration.id]: {
                                    ...integrationVars,
                                    [envVar.key]: e.target.value
                                  }
                                })}
                                required={envVar.required}
                                className="font-mono text-sm"
                              />
                              {envVar.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">{envVar.description}</p>
                              )}
                            </div>
                          ))}

                          <Button
                            type="button"
                            onClick={() => handleManualConnect(integration)}
                            disabled={isSubmitting === integration.id}
                            className="w-full"
                          >
                            {isSubmitting === integration.id ? t('integrations.buttons.connecting') : t('integrations.buttons.connect')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('integrations.dialogs.disconnectLiveTitle')}</AlertDialogTitle>
            <AlertDialogDescription dangerouslySetInnerHTML={{
              __html: t('integrations.dialogs.disconnectLiveDescription', { name: integrationToDisconnect?.template?.name || integrationToDisconnect?.integration_type })
            }} />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('integrations.dialogs.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDisconnect} className="bg-red-600 hover:bg-red-700">
              {t('integrations.buttons.disconnect')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
